import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useDropzone } from 'react-dropzone'
import { Paper } from '@mui/material'

import UploadFileIcon from '@mui/icons-material/UploadFile'

import { theme } from '../../themes/theme'
import { MUTED_ICON, MUTED_TEXT_DARK } from '../styles/colors'
import { CenteredFlexColumn } from '../styles/Containers'
import { ErrorMessage } from '../common/UserMessage'
import BodyText from '../styles/Text'
import Button from '../common/Button'
import Loading from '../common/Loading'

export const uploadFileStrings = {
  quietPrompt: 'Drag files here or click to browse'
}

const StyledPaper = styled(Paper)`
  &.MuiPaper-root {
    border: ${({ error, quiet }) =>
      error ? `1px solid ${theme.colors.error}` : `1px solid ${quiet === 'true' ? MUTED_ICON : theme.colors.primary}`};
    border-radius: ${theme.borderRadius.div};
    background: none;
  }

  .MuiSvgIcon-root {
    height: 28px;
    width: 28px;
    color: ${({ quiet }) => (quiet === 'true' ? MUTED_TEXT_DARK : theme.colors.primary)};
  }

  ${({ quiet }) => quiet === 'true' && 'cursor: pointer;'}

  display: flex;
  gap: ${({ condensed }) => (condensed === 'false' ? '15px' : '5px')};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;

  padding: ${({ condensed }) => (condensed === 'false' ? '30px' : '10px')};
`

const Muted = styled(BodyText)`
  color: ${MUTED_TEXT_DARK};
`

const LoadingContainer = styled(CenteredFlexColumn)`
  border: 0.775px solid ${theme.colors.primaryLight};
  border-radius: ${theme.borderRadius.div};
  padding: ${({ condensed }) => (condensed === 'false' ? '30px' : '10px')};
`

function UploadFile({ onUpload = () => {}, errorFileNames = [], loading = false, maxFiles = 0, condensed = false, quiet = false }) {
  const { getRootProps, getInputProps, isDragActive, open, fileRejections } = useDropzone({
    onDrop: onUpload,
    // The quiet variant is one muted prompt line; the whole zone is the click target.
    noClick: !quiet,
    maxFiles,
    multiple: maxFiles !== 1,
    accept: file => {
      if (maxFiles !== 1 && file.fullPath !== undefined && (file.fullPath.includes('/') || file.fullPath.includes('\\'))) {
        return 'File folders are not accepted. Please upload an individual document.'
      }
      return
    }
  })
  const { ref, ...rootProps } = getRootProps()

  if (loading) {
    return (
      <LoadingContainer condensed={condensed.toString()}>
        <Loading>Uploading...</Loading>
      </LoadingContainer>
    )
  }

  return (
    <>
      <StyledPaper
        {...rootProps}
        variant="outlined"
        ref={ref}
        error={errorFileNames.length || fileRejections.length ? 'true' : undefined}
        condensed={condensed.toString()}
        quiet={quiet.toString()}>
        <input {...getInputProps()} id="file-upload" aria-label="Upload files" />
        <UploadFileIcon />
        {isDragActive ? (
          <>Drop your file here</>
        ) : quiet ? (
          <Muted>{uploadFileStrings.quietPrompt}</Muted>
        ) : (
          <>
            <BodyText>
              Drag and drop {maxFiles === 1 && 'a '}file{maxFiles !== 1 && 's'} to upload or
            </BodyText>
            <Button onClick={open}>Browse</Button>
          </>
        )}
      </StyledPaper>
      {errorFileNames.length > 0 && (
        <ErrorMessage>There was an error uploading the following file(s): {errorFileNames.map(f => f).join(', ')}. Please try again.</ErrorMessage>
      )}
      {fileRejections.length > 0 && <ErrorMessage>File folders are not accepted. Please upload an individual document.</ErrorMessage>}
    </>
  )
}

UploadFile.propTypes = {
  onUpload: PropTypes.func,
  errorFileNames: PropTypes.array,
  loading: PropTypes.bool,
  maxFiles: PropTypes.number,
  condensed: PropTypes.bool,
  // Muted border/icon and one prompt line, whole zone clickable.
  quiet: PropTypes.bool
}

export default UploadFile
